// src/pages/Users/Register.jsx
import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Alert, Card, Row, Col, Spinner } from 'react-bootstrap';
import { authAPI, universityAPI } from '../../api/wbuoaApi'; // Import universityAPI
import { useNavigate } from 'react-router-dom';

const UserRegister = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    emp_name: '',
    role: '',
    user_id: '',
    office_bearer: '',
    designation: '',
    university: '',
    mobile: ''
  });
  
  const [universities, setUniversities] = useState([]);
  const [loadingUniversities, setLoadingUniversities] = useState(true);
  const [universitiesError, setUniversitiesError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const navigate = useNavigate();

  // Fetch universities using the universityAPI
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        setLoadingUniversities(true);
        // Use universityAPI to fetch universities
        const response = await universityAPI.listUniversities();
        
        // Handle different response structures
        let universitiesData = [];
        if (Array.isArray(response)) {
          universitiesData = response;
        } else if (response.data && Array.isArray(response.data)) {
          universitiesData = response.data;
        } else if (response.results && Array.isArray(response.results)) {
          universitiesData = response.results;
        } else {
          throw new Error('Unexpected universities response format');
        }
        
        setUniversities(universitiesData);
      } catch (err) {
        console.error("University fetch error:", err);
        setUniversitiesError('Failed to load universities. ' + (err.message || 'Please try again later.'));
      } finally {
        setLoadingUniversities(false);
      }
    };

    fetchUniversities();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    
    try {
      // Use the API method for registration
      await authAPI.register(formData);
      navigate('/users/login');
    } catch (err) {
      setSubmitError('Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="register-page">
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Card className="register-card">
          <Card.Body className="p-4">
            <div className="text-center mb-4">
              <div className="register-logo mb-3">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z" stroke="#e0f2e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="register-title">Create an Account</h2>
              <p className="register-subtitle">Join the WBUOA Admin Portal</p>
            </div>
            
            {submitError && <Alert variant="danger" className="text-center">{submitError}</Alert>}
            
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="emp_name"
                      value={formData.emp_name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      className="form-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Enter your email"
                      className="form-input"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      placeholder="Create a password"
                      className="form-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">User ID</Form.Label>
                    <Form.Control
                      type="text"
                      name="user_id"
                      value={formData.user_id}
                      onChange={handleChange}
                      required
                      placeholder="Enter your user ID"
                      className="form-input"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">Role</Form.Label>
                    <Form.Select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                      className="form-input"
                    >
                      <option value="">Select Role</option>
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">Office Bearer</Form.Label>
                    <Form.Select
                      name="office_bearer"
                      value={formData.office_bearer}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Select Office Bearer</option>
                      <option value="secretary">Secretary</option>
                      <option value="president">President</option>
                      <option value="member">Member</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">Designation</Form.Label>
                    <Form.Control
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      placeholder="Enter your designation"
                      className="form-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">University</Form.Label>
                    
                    {universitiesError && (
                      <Alert variant="warning" className="py-1 mb-2">
                        {universitiesError}
                      </Alert>
                    )}
                    
                    {loadingUniversities ? (
                      <div className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Loading universities...</span>
                      </div>
                    ) : (
                      <Form.Select
                        name="university"
                        value={formData.university}
                        onChange={handleChange}
                        required
                        className="form-input"
                      >
                        <option value="">Select University</option>
                        {universities.length > 0 ? (
                          universities.map((univ) => (
                            <option key={univ.id || univ.University} value={univ.University}>
                              {univ.University}
                            </option>
                          ))
                        ) : (
                          <option disabled>No universities available</option>
                        )}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-4">
                <Form.Label className="form-label">Mobile Number</Form.Label>
                <Form.Control
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Enter your mobile number"
                  className="form-input"
                />
              </Form.Group>
              
              <Button 
                variant="success" 
                type="submit" 
                className="w-100 register-btn"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Registering...
                  </>
                ) : 'Create Account'}
              </Button>
            </Form>
            
            <div className="text-center mt-4">
              <p className="login-text">
                Already have an account? <a href="/users/login" className="login-link">Sign in</a>
              </p>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default UserRegister;